<template>
  <div
    ref="root"
    class="sync-height"
    :data-group-id="groupId"
    :data-sync-disabled="disabled ? 'true' : 'false'"
  >
    <slot />
  </div>
</template>

<script>
export default {
  name: 'SyncHeight',
  props: {
    groupId: {
      type: String,
      default: '',
    },
    scopeSelector: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resizeObserver: null,
      mutationObserver: null,
      syncRafId: null,
      lastGroupId: '',
    };
  },
  mounted() {
    this.lastGroupId = this.groupId;
    if (!this.disabled) {
      this.syncHeight();
      this.setupObservers();
    }
  },
  beforeUnmount() {
    this.resetHeights(this.groupId);
    this.cleanupObservers();
    this.cancelScheduledSync();
  },
  watch: {
    groupId(newGroupId, oldGroupId) {
      this.lastGroupId = newGroupId;
      if (oldGroupId) {
        this.resetHeights(oldGroupId);
      }
      this.$nextTick(() => {
        this.scheduleSync();
      });
    },
    disabled(isDisabled) {
      if (!isDisabled) {
        this.$nextTick(() => {
          this.syncHeight();
          this.setupObservers();
        });
      } else {
        this.cancelScheduledSync();
        this.cleanupObservers();
        this.resetHeights(this.groupId, { onlySelf: true });
      }
    },
  },
  methods: {
    getScopeRoot() {
      if (!this.scopeSelector) return document;
      return document.querySelector(this.scopeSelector) || document;
    },
    scheduleSync(force = false) {
      if (typeof window === 'undefined') return;
      if (this.disabled && !force) return;
      this.cancelScheduledSync();
      this.syncRafId = window.requestAnimationFrame(() => {
        this.syncRafId = null;
        this.syncHeight(force);
      });
    },
    cancelScheduledSync() {
      if (this.syncRafId) {
        window.cancelAnimationFrame(this.syncRafId);
        this.syncRafId = null;
      }
    },
    syncHeight(force = false) {
      if (this.disabled && !force) return;
      if (!this.groupId || typeof document === 'undefined') return;

      const allComponents = this.getScopeRoot().querySelectorAll(
        `.sync-height[data-group-id="${this.groupId}"][data-sync-disabled="false"]`,
      );
      let maxHeight = 0;

      Array.prototype.forEach.call(allComponents, (component) => {
        const previousHeight = component.style.height;
        component.style.height = 'auto';
        const rect = component.getBoundingClientRect();
        const measuredHeight = rect.height;
        component.style.height = previousHeight;

        if (measuredHeight > maxHeight) {
          maxHeight = measuredHeight;
        }
      });

      Array.prototype.forEach.call(allComponents, (component) => {
        component.style.height = `${maxHeight}px`;
      });
    },
    resetHeights(groupId, options = {}) {
      if (!groupId || typeof document === 'undefined') return;
      if (options.onlySelf && this.$refs.root) {
        this.$refs.root.style.height = '';
        if (groupId === this.groupId) {
          this.scheduleSync(true);
        }
        return;
      }
      const allComponents = this.getScopeRoot().querySelectorAll(
        `.sync-height[data-group-id="${groupId}"]`,
      );
      Array.prototype.forEach.call(allComponents, (component) => {
        component.style.height = '';
      });
      if (groupId === this.groupId) {
        this.scheduleSync();
      }
    },
    setupObservers() {
      if (!this.$refs.root || typeof window === 'undefined') return;
      if (this.disabled) return;
      if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleSync();
      });

      this.$nextTick(() => {
        this.observeChildren();
      });

      this.mutationObserver = new MutationObserver(() => {
        this.observeChildren();
        this.scheduleSync();
      });

      this.mutationObserver.observe(this.$refs.root, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    },
    observeChildren() {
      if (!this.resizeObserver || !this.$refs.root) return;
      this.resizeObserver.disconnect();
      const elements = this.$refs.root.querySelectorAll('*');
      Array.prototype.forEach.call(elements, (el) => {
        this.resizeObserver.observe(el);
      });
    },
    cleanupObservers() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    },
  },
};
</script>
